import {z} from '@hono/zod-openapi';

import {ASSISTANT_ACTIVE_GRID_CODE_MAX_CHARACTERS} from '../constants/ASSISTANT_ACTIVE_GRID_CODE_MAX_CHARACTERS';
import {ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS} from '../constants/ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS';
import {ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS} from '../constants/ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS';
import {ASSISTANT_GRID_FILTERS_MAX_ENTRIES} from '../constants/ASSISTANT_GRID_FILTERS_MAX_ENTRIES';
import {ASSISTANT_GRID_SORT_COLUMN_KEY_MAX_CHARACTERS} from '../constants/ASSISTANT_GRID_SORT_COLUMN_KEY_MAX_CHARACTERS';
import {ASSISTANT_GRID_SORT_MAX_ITEMS} from '../constants/ASSISTANT_GRID_SORT_MAX_ITEMS';
import {ASSISTANT_LANGUAGE_MAX_CHARACTERS} from '../constants/ASSISTANT_LANGUAGE_MAX_CHARACTERS';
import {ASSISTANT_LOCATION_MAX_ENTRIES} from '../constants/ASSISTANT_LOCATION_MAX_ENTRIES';
import {ASSISTANT_LOCATION_PATH_MAX_CHARACTERS} from '../constants/ASSISTANT_LOCATION_PATH_MAX_CHARACTERS';
import {ASSISTANT_LOCATION_VALUE_MAX_CHARACTERS} from '../constants/ASSISTANT_LOCATION_VALUE_MAX_CHARACTERS';
import {ASSISTANT_REACHABLE_ROUTE_ID_MAX_CHARACTERS} from '../constants/ASSISTANT_REACHABLE_ROUTE_ID_MAX_CHARACTERS';
import {ASSISTANT_REACHABLE_ROUTE_IDS_MAX_ITEMS} from '../constants/ASSISTANT_REACHABLE_ROUTE_IDS_MAX_ITEMS';
import {assistantAutomationRuleOnScreenSchema} from './AssistantAutomationRuleOnScreenSchema';
import {assistantBusinessCaseContextSchema} from './AssistantBusinessCaseContextSchema';
import {assistantEntityContextSchema} from './AssistantEntityContextSchema';
import {assistantInterestContextSchema} from './AssistantInterestContextSchema';
import {assistantRentalOrderContextSchema} from './AssistantRentalOrderContextSchema';
import {assistantTemplateEditorContextSchema} from './AssistantTemplateEditorContextSchema';

const locationParamsSchema = z
  .record(z.string(), z.string().max(ASSISTANT_LOCATION_VALUE_MAX_CHARACTERS))
  .refine(
    (record) => Object.keys(record).length <= ASSISTANT_LOCATION_MAX_ENTRIES,
    `Too many entries (maximum ${ASSISTANT_LOCATION_MAX_ENTRIES})`
  );

/**
 * Flat, sanitized snapshot of where the user is, sent with each assistant message.
 *
 * Deliberately flat (no per-field {value, trust} wrapper) and minimal for the MVP —
 * the shape is expected to evolve additively (selectedRowIds, availableActions,
 * reachableRouteIds arrive with later capability tickets). Server-authoritative fields
 * (tenantId, dealerId, permissions, reachableRouteIds) are intentionally NOT collected
 * here — prompt-bridge derives/verifies them and treats this whole snapshot as untrusted
 * model input. See ai-assistant-context-plan.md §4.1 / §4.6.
 */
export const assistantContextSnapshotSchema = z
  .object({
    location: z.object({
      path: z.string().min(1).max(ASSISTANT_LOCATION_PATH_MAX_CHARACTERS), // location.pathname
      query: locationParamsSchema, // route path params (:id segments); {} when none
      search: locationParamsSchema, // parsed URL search params (?key=value)
    }),
    // BCP-47 tag; capped because it drives Intl formatter construction for every rendered cell.
    language: z.string().min(1).max(ASSISTANT_LANGUAGE_MAX_CHARACTERS),
    // Untrusted scope hint only; backend authorization always determines branch access.
    currentBranch: z
      .object({
        id: z.string().min(1).max(ASSISTANT_ENTITY_CONTEXT_ID_MAX_CHARACTERS),
        label: z.string().min(1).max(ASSISTANT_ENTITY_CONTEXT_LABEL_MAX_CHARACTERS),
      })
      .optional(),
    /**
     * Route-catalog IDs the client's permission-filtered register resolved as reachable for the
     * current user. Untrusted, narrow-only input: the server INTERSECTS it with the static route
     * catalog and never widens beyond it, so a manipulated list can only shrink or misdirect the
     * user's own suggestions — DMS route guards re-validate at navigation time. Absent on clients
     * below contract 0.16.0; navigation tooling is disabled without it.
     */
    reachableRouteIds: z
      .array(z.string().min(1).max(ASSISTANT_REACHABLE_ROUTE_ID_MAX_CHARACTERS))
      .max(ASSISTANT_REACHABLE_ROUTE_IDS_MAX_ITEMS)
      .optional(),
    /**
     * Related records the mounted page has loaded, keyed by entity kind. Lets the assistant propose
     * a route for a related entity (a business case's vehicle, its customer) without ever reusing
     * the current URL's id for another entity type. Absent when the page publishes no related
     * entity; cross-entity routes are then simply not proposable. See
     * {@link assistantEntityContextSchema} for the trust and lifecycle contract.
     */
    entities: assistantEntityContextSchema.optional(),
    activeGrid: z
      .object({
        gridCode: z.string().min(1).max(ASSISTANT_ACTIVE_GRID_CODE_MAX_CHARACTERS),
        // userControlled — injection surface, narrowed by the filter ticket + server-side
        filters: z
          .record(z.string(), z.unknown())
          .refine(
            (record) => Object.keys(record).length <= ASSISTANT_GRID_FILTERS_MAX_ENTRIES,
            `Too many filters (maximum ${ASSISTANT_GRID_FILTERS_MAX_ENTRIES})`
          ),
        sort: z
          .array(
            z.object({
              columnKey: z.string().min(1).max(ASSISTANT_GRID_SORT_COLUMN_KEY_MAX_CHARACTERS),
              direction: z.enum(['asc', 'desc']),
            })
          )
          .max(ASSISTANT_GRID_SORT_MAX_ITEMS),
      })
      .optional(),
    // Present only while the DMS Document Template Editor page is mounted. The whole slice is
    // untrusted model input and must not grant authority or bypass server-side validation.
    templateEditor: assistantTemplateEditorContextSchema.optional(),
    // Present only while a sales-interest detail page is mounted. Carries the
    // interest's communication timeline, which no server-side path can read. Untrusted model input.
    interest: assistantInterestContextSchema.optional(),
    // Present only while a supported SELLING or BUYING business-case detail page is mounted.
    businessCase: assistantBusinessCaseContextSchema.optional(),
    // Present only while a rental-order detail page is mounted. Untrusted model input.
    rentalOrder: assistantRentalOrderContextSchema.optional(),
    // Present only while an automation rule editor or detail page is mounted. Untrusted model input.
    automationRuleOnScreen: assistantAutomationRuleOnScreenSchema.optional(),
    recentErrors: z.array(
      z.object({
        time: z.string().min(1),
        source: z.enum(['browser', 'api', 'crash']),
        route: z.string().min(1).optional(), // failed API route path (api source only)
        message: z.string().min(1),
        status: z.number().optional(),
      })
    ),
  })
  .openapi('AssistantContextSnapshot');

export type AssistantContextSnapshot = z.infer<typeof assistantContextSnapshotSchema>;
